/**
 * TMSLayer
 *
 * Implementation for TMS (Tile Map Service) raster layers.
 * TMS layers use tile coordinates ({z}/{x}/{y}) to fetch raster tiles.
 *
 * Expected config structure:
 * {
 *   "tileUrl": "https://example.com/tiles/{z}/{x}/{y}.png",  // Tile URL template
 *   "tileSize": 256,                                          // Tile size (default: 256)
 *   "scheme": "xyz",                                          // Tile scheme: xyz or tms (default: xyz)
 *   "minzoom": 0,                                             // Min zoom (default: 0)
 *   "maxzoom": 22,                                            // Max zoom (default: 22)
 *   "bounds": [-180, -85, 180, 85],                          // Optional bounding box
 *   "attribution": "© Example"                                // Optional attribution
 * }
 *
 * Note: The main difference between TMS and XYZ is the Y-axis direction.
 * TMS uses bottom-left origin, XYZ uses top-left origin (standard for web maps).
 */

import mapboxgl from 'mapbox-gl';
import { BaseLayer, LayerConfig } from './BaseLayer';
import { visibilityAwareInterval, VisibilityAwareIntervalHandle } from './utils/visibilityAwareInterval';
import { isInRefreshTimeRange } from './utils/refreshTimeRange';
import { cacheBustKeyForInterval } from './WMSLayer';
import { logger } from '../../utils/logger';
import { getBackendUrl } from '../../services/configService';

const log = logger('TMSLayer');

export class TMSLayer extends BaseLayer {
  /**
   * Mapbox source ID for this layer
   */
  private sourceId: string;

  /**
   * Mapbox layer ID for this layer
   */
  private layerId: string;

  /**
   * Auto-refresh timer (visibility-aware).
   */
  private refreshHandle: VisibilityAwareIntervalHandle | null = null;

  /**
   * Constructor
   *
   * @param map Mapbox GL map instance
   * @param config Layer configuration from database
   */
  constructor(map: mapboxgl.Map, config: LayerConfig) {
    super(map, config);
    this.sourceId = `tms-source-${config.id}`;
    this.layerId = `tms-layer-${config.id}`;
  }

  /**
   * Initialize TMS layer
   */
  protected async onInitialize(): Promise<void> {
    // Validate required config
    if (!this.config.config.tileUrl && !this.config.source_url) {
      throw new Error('TMS layer requires config.tileUrl or source_url');
    }

    // Validate tile URL has required placeholders
    const tileUrl = this.getTileUrl();
    if (!tileUrl.includes('{x}') || !tileUrl.includes('{y}') || !tileUrl.includes('{z}')) {
      throw new Error('TMS tile URL must contain {x}, {y}, and {z} placeholders');
    }
  }

  /**
   * Enable TMS layer on map
   */
  protected async onEnable(): Promise<void> {
    const tileUrl = this.getTileUrl();
    const config = this.config.config;

    log.debug(`onEnable: ${this.config.name}`);

    // Add source if it doesn't exist
    if (!this.map.getSource(this.sourceId)) {
      const sourceConfig: mapboxgl.RasterSourceSpecification = {
        type: 'raster',
        tiles: [tileUrl],
        tileSize: config.tileSize || 256,
        scheme: config.scheme || 'xyz'
      };

      // Add optional properties
      if (config.minzoom !== undefined) {
        sourceConfig.minzoom = config.minzoom;
      }

      if (config.maxzoom !== undefined) {
        sourceConfig.maxzoom = config.maxzoom;
      }

      if (config.bounds) {
        sourceConfig.bounds = config.bounds;
      }

      if (config.attribution) {
        sourceConfig.attribution = config.attribution;
      }

      this.map.addSource(this.sourceId, sourceConfig);
    } else {
      // Source exists — re-eval cache-bust URL on toggle-on. Same rationale
      // as WMSLayer: in-memory tile cache otherwise serves stale tiles
      // across polling-tick boundaries.
      const src = this.map.getSource(this.sourceId) as mapboxgl.RasterTileSource | undefined;
      const currentTiles = (src as any)?.tiles as string[] | undefined;
      if (src && currentTiles?.[0] !== tileUrl) {
        src.setTiles([tileUrl]);
        log.debug(`onEnable: tile URL changed, forced refresh via setTiles`);
      }
    }

    // Add layer if it doesn't exist
    if (!this.map.getLayer(this.layerId)) {
      const layerDef: mapboxgl.RasterLayer = {
        id: this.layerId,
        type: 'raster',
        source: this.sourceId,
        minzoom: this.config.zoom_min,
        maxzoom: this.config.zoom_max,
        paint: {
          'raster-opacity': this.state.currentOpacity,
          'raster-fade-duration': 300
        }
      };

      // Add raster resampling if configured
      if (config.rasterResampling) {
        layerDef.paint!['raster-resampling'] = config.rasterResampling;
      }

      this.map.addLayer(layerDef);
    } else {
      this.map.setLayoutProperty(this.layerId, 'visibility', 'visible');
    }

    // Start auto-refresh if configured
    const refreshInterval = this.config.config.refreshInterval;
    if (refreshInterval && refreshInterval > 0) {
      this.startAutoRefresh(refreshInterval);
      log.debug(`onEnable: auto-refresh started, every ${refreshInterval}ms`);
    }
  }

  /**
   * Disable TMS layer on map
   */
  protected async onDisable(): Promise<void> {
    this.stopAutoRefresh();
    if (this.map.getLayer(this.layerId)) {
      this.map.setLayoutProperty(this.layerId, 'visibility', 'none');
    }
  }

  /**
   * Update layer opacity
   *
   * @param opacity Opacity value (0-1)
   */
  protected async onSetOpacity(opacity: number): Promise<void> {
    if (this.map.getLayer(this.layerId)) {
      this.map.setPaintProperty(this.layerId, 'raster-opacity', opacity);
    }
  }

  /**
   * Update layer order
   *
   * @param order Order value (higher = on top)
   */
  protected onSetOrder(order: number): void {
    if (this.map.getLayer(this.layerId)) {
      // Move layer to appropriate position
      // Implementation similar to WMSLayer
    }
  }

  /**
   * Destroy layer and clean up resources
   */
  protected async onDestroy(): Promise<void> {
    this.stopAutoRefresh();

    // Remove layer
    if (this.map.getLayer(this.layerId)) {
      this.map.removeLayer(this.layerId);
    }

    // Remove source
    if (this.map.getSource(this.sourceId)) {
      this.map.removeSource(this.sourceId);
    }
  }

  /**
   * Manually refresh by re-evaluating the tile URL (with current cache-bust
   * key) and calling setTiles. Gated by refreshTimeRange when configured.
   */
  private refreshTiles(): void {
    const range = this.config.config.refreshTimeRange;
    if (range && !isInRefreshTimeRange(range)) {
      log.debug(`refresh skipped — outside refreshTimeRange ${range}`);
      return;
    }
    const src = this.map.getSource(this.sourceId) as mapboxgl.RasterTileSource | undefined;
    if (!src) return;
    src.setTiles([this.getTileUrl()]);
    log.debug(`refresh: setTiles`);
  }

  private startAutoRefresh(intervalMs: number): void {
    this.stopAutoRefresh();
    this.refreshHandle = visibilityAwareInterval(() => this.refreshTiles(), intervalMs);
  }

  private stopAutoRefresh(): void {
    if (this.refreshHandle) {
      this.refreshHandle.cancel();
      this.refreshHandle = null;
    }
  }

  /**
   * Get Mapbox layer IDs
   */
  public getMapboxLayerIds(): string[] {
    return [this.layerId];
  }

  /**
   * Get Mapbox source IDs
   */
  public getMapboxSourceIds(): string[] {
    return [this.sourceId];
  }

  /**
   * Get tile URL (from config.tileUrl or source_url)
   *
   * @returns Tile URL template
   */
  private getTileUrl(): string {
    // Get backend URL for production/staging (where frontend and backend are on different domains)
    const backendUrl = getBackendUrl() || '';

    let tileUrl = this.config.config.tileUrl || this.config.source_url || '';

    // CRITICAL: In production/staging, prepend backend URL to relative paths
    // This fixes the issue where relative URLs resolve to frontend origin instead of backend
    if (tileUrl.startsWith('/') && backendUrl) {
      tileUrl = backendUrl + tileUrl;
    }

    // Append cache-bust param when polling is configured, so each tick window
    // produces a distinct URL Mapbox treats as fresh.
    const refreshInterval = this.config.config.refreshInterval;
    if (refreshInterval && refreshInterval > 0) {
      const sep = tileUrl.includes('?') ? '&' : '?';
      tileUrl = `${tileUrl}${sep}_cb=${cacheBustKeyForInterval(refreshInterval)}`;
    }

    return tileUrl;
  }
}
